import { dataAverageTimeSeries } from "api/data";
import { ResponseData } from "api/types/api";
import { DataReqBody } from "api/types/data";
import { Device } from "api/types/device";
import { useEffect, useState } from "react";

import { formatDate, formatDateToZ } from "util/datelib";
import DatePickerField from "widgets/shared/inputs/datePicker";
import PropagateLoader from "react-spinners/PropagateLoader";
import { t } from "i18next";
import { DeviceConfiguration } from "models/devices/configuration";
import {
  buildAverageTimeSeriesRequestBody,
  buildLoraHumidityTemperatureTimeSeriesBody,
  getChartColors,
  processData,
} from "util/device";
import { TimeSeriesChartDropdown } from "models/dropdowns/timeSeriesChart";
import DeviceCard from "widgets/cards/device";
import NoDataSvg from "assets/svg/no-data.svg";
import BarGraph from "widgets/charts/bar";
import { ChartDataset, ChartDatasetSeries } from "widgets/charts/models";
import GenericButton from "widgets/controls/buttons/generic";

interface GeneralProps {
  device?: Device;
  config?: DeviceConfiguration;
  height?: number;
}

type LoraHumidityRaw = {
  labels: string[];
  setAvg: number[];
  setMinMax: number[][];
  setTriggerHigh: number[];
  setTriggerLow: number[];
  setTempAvg: (number | null)[];
};

function chartDatasetHasPlottableValues(ds: ChartDatasetSeries): boolean {
  const { data } = ds;
  if (!Array.isArray(data) || data.length === 0) return false;
  const first = data[0] as unknown;
  if (Array.isArray(first)) {
    return (data as number[][]).some(
      (row) => Array.isArray(row) && row.some((n) => Number.isFinite(n))
    );
  }
  return (data as number[]).some((v) => Number.isFinite(v as number));
}

function buildLoraHumidityDatasets(
  raw: LoraHumidityRaw,
  device?: Device
): ChartDatasetSeries[] {
  const hasFiniteTemp = raw.setTempAvg.some((v) => Number.isFinite(v as number));
  const humUnit = t("device.units", { context: "humidity" });
  const tempUnit = t("device.units", { context: "temperature" });
  const useDualAxis = hasFiniteTemp;
  const yHum = useDualAxis ? "y" : undefined;
  const yTemp = useDualAxis ? "y1" : undefined;

  const dataSets: ChartDatasetSeries[] = [];

  dataSets.push({
    label: hasFiniteTemp
      ? t("chart.average.timeseries.legend.one_humidity")
      : t("device.tooltip.average"),
    data: raw.setAvg,
    backgroundColor: "#8eeBaa",
    borderColor: "#8eeBaa",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    type: "line" as const,
    order: 0,
    pointRadius: 3,
    pointHoverRadius: 6,
    pointHitRadius: 16,
    pointBackgroundColor: "#525252",
    pointBorderColor: "#404040",
    pointBorderWidth: 2,
    yAxisID: yHum,
    unit: humUnit,
    unitContext: "humidity",
  });

  if (device?.triggerHigh) {
    dataSets.push({
      label: t("device.tooltip.trigger.high"),
      data: raw.setTriggerHigh,
      backgroundColor: "#f87171",
      borderColor: "#f87171",
      borderWidth: 2,
      borderRadius: 5,
      borderSkipped: false,
      type: "line" as const,
      order: 5,
      pointRadius: 0,
      yAxisID: yHum,
      unit: humUnit,
      unitContext: "humidity",
    });
  }

  if (device?.triggerLow || device?.lowLevelPercentage) {
    dataSets.push({
      label: t("device.tooltip.trigger.low"),
      data: raw.setTriggerLow,
      backgroundColor: "#f87171",
      borderColor: "#f87171",
      borderWidth: 2,
      borderRadius: 5,
      borderSkipped: false,
      type: "line" as const,
      order: 5,
      pointRadius: 0,
      yAxisID: yHum,
      unit: humUnit,
      unitContext: "humidity",
    });
  }

  dataSets.push({
    label: t("device.tooltip.max.min"),
    data: raw.setMinMax,
    backgroundColor: "#50cfe7",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    order: 1,
    yAxisID: yHum,
    unit: humUnit,
    unitContext: "humidity",
  });

  if (hasFiniteTemp) {
    dataSets.push({
      label: t("chart.average.timeseries.legend.one_temperature"),
      data: raw.setTempAvg,
      backgroundColor: "#f97316",
      borderColor: "#f97316",
      borderWidth: 2,
      borderRadius: 5,
      borderSkipped: false,
      type: "line" as const,
      order: 2,
      pointRadius: 3,
      pointHoverRadius: 6,
      pointHitRadius: 16,
      pointBackgroundColor: "#525252",
      pointBorderColor: "#404040",
      pointBorderWidth: 2,
      yAxisID: yTemp,
      unit: tempUnit,
      unitContext: "temperature",
      spanGaps: true,
    });
  }

  return dataSets;
}

const Timeseries: React.FC<GeneralProps> = ({ device, config, height }) => {
  const { color0 } = getChartColors(config || undefined);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  const [chartData, setChartData] = useState<ChartDataset>({
    labels: [],
    datasets: [
      {
        label: "set0",
        data: [],
        backgroundColor: color0,
      },
    ],
  });

  const [loraHumidityRaw, setLoraHumidityRaw] = useState<LoraHumidityRaw | null>(
    null
  );

  const [date, setDate] = useState<any>(new Date());
  const [filter, setFilter] = useState<any>({
    calendar: "year",
    xFormat: "month",
    currentBucket: "day",
  });

  const setXFormat = (filter: string) => {
    if (filter === "month") {
      setDate(new Date());
      setFilter({ calendar: filter, xFormat: "day", currentBucket: "hour" });
    } else if (filter === "year") {
      setFilter({ calendar: filter, xFormat: "month", currentBucket: "day" });
    } else {
      setFilter({ calendar: filter, xFormat: "year", currentBucket: "month" });
    }
  };

  const fetchData = async () => {
    if (!device?.snr || !config) {
      return;
    }

    setLoading(true);

    const partial = {
      snr: device.snr,
      from: formatDateToZ(date, false, filter.xFormat, device?.timezone),
      to: formatDateToZ(date, true, filter.xFormat, device?.timezone),
      bucket: filter.currentBucket,
    };

    setStartDate(partial.from);
    setEndDate(partial.to);

    if (config.model === "lora.humidity") {
      const humReq = buildAverageTimeSeriesRequestBody(partial, config);
      const tempReq = buildLoraHumidityTemperatureTimeSeriesBody(partial);

      const [humSettled, tempSettled] = await Promise.allSettled([
        dataAverageTimeSeries(humReq),
        dataAverageTimeSeries(tempReq),
      ]);

      const humRes: ResponseData =
        humSettled.status === "fulfilled"
          ? humSettled.value
          : {
              error: "failed",
              status: 0,
              data: undefined,
              isLoading: false,
            };

      if (
        humRes.error ||
        humRes.status !== 200 ||
        !Array.isArray(humRes.data)
      ) {
        setLoraHumidityRaw(null);
        setChartData({ labels: [], datasets: [] });
        setLoading(false);
        return;
      }

      const newHumData = humRes.data as {
        date: string;
        min?: number;
        max?: number;
        avg?: number;
      }[];

      const newTempData =
        tempSettled.status === "fulfilled" &&
        !tempSettled.value.error &&
        tempSettled.value.status === 200 &&
        Array.isArray(tempSettled.value.data)
          ? (tempSettled.value.data as { date: string; avg?: number }[])
          : [];

      const tempByDate = new Map(
        newTempData.map((el) => [el.date, el.avg ?? null])
      );

      const labels: string[] = [];
      const setAvg: number[] = [];
      const setTriggerHigh: number[] = [];
      const setTriggerLow: number[] = [];
      const setMinMax: number[][] = [];
      const setTempAvg: (number | null)[] = [];

      for (let index = 0; index < newHumData.length; index++) {
        const element = newHumData[index];
        const label = formatDate(element.date, filter.currentBucket);

        const usage = [element.min ?? 0, element.max ?? 0];

        const min = device?.triggerLow || device?.lowLevelPercentage || 0;
        const max = device?.triggerHigh || 0;
        const avg = element.avg || 0;

        labels.push(label);
        setAvg.push(avg);
        setMinMax.push(usage);
        setTriggerHigh.push(max);
        setTriggerLow.push(min);

        const tv = tempByDate.get(element.date);
        setTempAvg.push(
          tv === undefined || tv === null ? null : (tv as number)
        );
      }

      setLoraHumidityRaw({
        labels,
        setAvg,
        setMinMax,
        setTriggerHigh,
        setTriggerLow,
        setTempAvg,
      });
      setLoading(false);
      return;
    }

    setLoraHumidityRaw(null);

    const req: DataReqBody = buildAverageTimeSeriesRequestBody(
      partial,
      config
    );

    const {
      data: newData,
      status,
      error,
      isLoading,
    } = await dataAverageTimeSeries(req);
    setLoading(isLoading);

    if (!error && status && status === 200) {
      const labels: string[] = [];
      const setAvg: number[] = [];
      const setTriggerHigh: number[] = [];
      const setTriggerLow: number[] = [];
      const setMinMax: number[][] = [];

      for (let index = 0; index < newData.length; index++) {
        const element = newData[index];
        const label = formatDate(element.date, filter.currentBucket);

        const usage = [
          config.units === "level"
            ? processData(device, element.max)
            : element.min,
          config.units === "level"
            ? processData(device, element.min)
            : element.max,
        ];

        const min = device?.triggerLow || device?.lowLevelPercentage || 0;
        const max = device?.triggerHigh || 0;
        const avg =
          config.units === "level"
            ? processData(device, element.avg)
            : element.avg || 0;

        labels.push(label);
        setAvg.push(avg);
        setMinMax.push(usage);
        setTriggerHigh.push(max);
        setTriggerLow.push(min);
      }

      const dataSets: ChartDatasetSeries[] = [];

      dataSets.push({
        label: t("device.tooltip.average"),
        data: setAvg,
        backgroundColor: "#8eeBaa",
        borderColor: "#8eeBaa",
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
        type: "line" as const,
        order: 0,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointHitRadius: 16,
        pointBackgroundColor: "#525252",
        pointBorderColor: "#404040",
        pointBorderWidth: 2,
      });

      if (device?.triggerHigh) {
        dataSets.push({
          label: t("device.tooltip.trigger.high"),
          data: setTriggerHigh,
          backgroundColor: "#f87171",
          borderColor: "#f87171",
          borderWidth: 2,
          borderRadius: 5,
          borderSkipped: false,
          type: "line" as const,
          order: 5,
          pointRadius: 0,
        });
      }

      if (device?.triggerLow || device?.lowLevelPercentage) {
        dataSets.push({
          label: t("device.tooltip.trigger.low"),
          data: setTriggerLow,
          backgroundColor: "#f87171",
          borderColor: "#f87171",
          borderWidth: 2,
          borderRadius: 5,
          borderSkipped: false,
          type: "line" as const,
          order: 5,
          pointRadius: 0,
        });
      }

      dataSets.push({
        label: t("device.tooltip.max.min"),
        data: setMinMax,
        backgroundColor: "#50cfe7",
        borderWidth: 2,
        borderRadius: 5,
        borderSkipped: false,
        order: 1,
      });

      setChartData({
        labels: labels,
        datasets: dataSets,
      });
    }
  };

  useEffect(() => {
    if (device?.snr) {
      fetchData();
    }
  }, [device, date, filter, config]);

  useEffect(() => {
    if (config?.model !== "lora.humidity" || !loraHumidityRaw) {
      return;
    }
    setChartData({
      labels: loraHumidityRaw.labels,
      datasets: buildLoraHumidityDatasets(loraHumidityRaw, device),
    });
  }, [loraHumidityRaw, device, config?.model]);

  const getDescription = () => {
    if (filter.calendar === "year") {
      return `${formatDate(startDate, "ddMMMyyyy")} - ${formatDate(
        endDate,
        "ddMMMyyyy"
      )}`;
    } else if (filter.calendar === "decade") {
      return `${formatDate(startDate, "MMMyyyy")} - ${formatDate(
        endDate,
        "MMMyyyy"
      )}`;
    } else if (filter.calendar === "month") {
      return `${formatDate(startDate, "ddMMMyyyy")}`;
    }
    return "";
  };

  const hasPlottedSeries =
    chartData.datasets.length > 0 &&
    chartData.datasets.some(chartDatasetHasPlottableValues);

  return (
    <DeviceCard
      title={t("chart.average.title", {
        context: filter.calendar,
      })}
      subTitle={getDescription()}
      topRight={
        <div className="flex w-full min-w-0 flex-shrink-0 flex-wrap items-center justify-end gap-2">
          {TimeSeriesChartDropdown.map((item) => (
            <GenericButton
              key={item.value}
              text={item.label}
              onClick={() => setXFormat(item.value)}
              size="sm"
              color="50"
              variant={filter.calendar === item.value ? "solid" : "outline"}
              rounded="rounded-md"
              tooltip={t("tooltip.xformat.filter", { context: item.value })}
              tooltipPlacement="bottom"
            />
          ))}
          <DatePickerField
            id={"1"}
            iconOnly
            sizes={"xs"}
            value={date}
            onChange={(val) => {
              setDate(val);
            }}
            maxDate={new Date()}
            maxDetail={filter.calendar}
          />
        </div>
      }
    >
      <div className="h-full min-h-72 w-full overflow-x-auto">
        {loading ? (
          <div className="mt-12 flex items-center justify-center bg-opacity-50">
            <PropagateLoader loading={loading} color="var(--color-500)" />
          </div>
        ) : hasPlottedSeries ? (
          <>
            <BarGraph
              id={"chartId"}
              data={chartData}
              config={config}
              stacked={false}
              height={height}
            />
          </>
        ) : (
          <div className="mt-4 flex justify-center">
            <img src={NoDataSvg} alt="Modal content" style={{ width: "12%" }} />
          </div>
        )}
      </div>
    </DeviceCard>
  );
};

export default Timeseries;
